/**
 * @file protected-layout.js
 * @description Layout protegido (CRA)
 */

import React from "react"
import { useNavigate } from "react-router-dom"
import useAuth from "../../../hooks/useAuth"
import { AuthProvider } from '../../../contexts/auth';
import { DialogProvider } from "../feedback/dialog/index.js"
import { LoadingProvider, LoadingSpinner } from "../feedback/loading/index.js"
import { NotificationProvider } from "../../../component/features/budget/components/notification/context/index.js"
import { AppLayout } from "./app-layout.js"
import { SidebarProvider } from "./sidebar/providers/sidebar-provider.js"
import { TooltipProvider } from "../ui/tooltip.js"; // fix import path

/**
 * Componente interno que verifica autenticação
 * @param {Object} props - Props do componente
 * @param {React.ReactNode} props.children - Componentes filhos
 * @returns {React.ReactElement} Layout protegido
 */
function ProtectedLayoutContent({ children }) {
    const { signed, loading } = useAuth()
    const navigate = useNavigate()

    React.useEffect(() => {
        if (!loading && !signed) {
            navigate("/login")
        }
    }, [signed, loading, navigate])

    if (loading) {
        return React.createElement(
            "div",
            {
                style: {
                    minHeight: "100vh",
                    background: "linear-gradient(to bottom right, #eff6ff, #e0e7ff)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                },
            },
            React.createElement(LoadingSpinner, { size: "lg", message: "Carregando..." })
        )
    }

    if (!signed) {
        return null
    }

    return React.createElement(
        LoadingProvider,
        null,
        React.createElement(
            DialogProvider,
            null,
            React.createElement(
                NotificationProvider,
                null,
                React.createElement(
                    TooltipProvider,
                    { delayDuration: 150 },
                    React.createElement(
                        SidebarProvider,
                        null,
                        React.createElement(AppLayout, { children })
                    )
                )
            )
        )
    )
}

/**
 * Layout protegido com AuthProvider
 * @param {Object} props - Props do componente
 * @param {React.ReactNode} props.children - Componentes filhos
 * @returns {React.ReactElement} Layout protegido
 */
export default function ProtectedLayout({ children }) {
    return React.createElement(
        AuthProvider,
        null,
        React.createElement(ProtectedLayoutContent, null, children)
    )
}
